import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { v2, v2api } from './v2'
import { useSessionStore } from '@/stores/sessionStore'
import { receiptKeys } from './receipts'

// Auto-generated run name shared by every "create run" entry point:
//   S<store.id>-<YYYYMMDD>/<HH:MM>   e.g. S9-20260620/14:30
// Runs are named automatically (the cashier never types one), so this is the
// single source of truth for the format.
export function buildRunName(storeId?: number): string {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  const date = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`
  const time = `${pad(now.getHours())}:${pad(now.getMinutes())}`
  return `S${storeId ?? '?'}-${date}/${time}`
}

// Mirrors DeliveryRunStatus on the BE (project/frontend/models/pos.py).
export type DeliveryRunStatus = 'open' | 'started' | 'completed' | 'cancelled'

export const DELIVERY_RUN_STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  started: 'Started',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

// One stop on a run — a delivery order with its (optimized) position.
// Shape mirrors DeliveryRunOrder.as_dict on the BE.
export interface DeliveryRunStop {
  run_order_id: number
  sequence: number | null
  receipt_id: number
  number: string | null
  total: string | null
  customer_name: string | null
  address: string | null
  city: string | null
  province: string | null
  zip: string | null
  delivery_notes: string | null
  delivery_status: string
  latitude: string | null
  longitude: string | null
}

// Google Maps hand-off payload (build_google_maps_url on the BE).
export interface DeliveryRunMaps {
  url: string | null
  truncated: boolean
  dropped: number
}

// Shape mirrors DeliveryRun.as_dict on the BE.
export interface DeliveryRun {
  id: number
  store_id: number
  name: string
  status: DeliveryRunStatus
  is_locked: boolean
  driver_id: number | null
  driver_name: string | null
  stop_count: number
  created_at: string | null
  started_at: string | null
  completed_at: string | null
  optimized_at: string | null
  route_polyline: string | null
  // Present on detail / mutation responses.
  stops?: DeliveryRunStop[]
  maps?: DeliveryRunMaps
  optimized?: boolean
  optimize_reason?: string | null
  added?: number[]
  rejected?: Array<{ receipt_id: number; reason: string; run_id?: number }>
}

export interface DeliveryRunDriver {
  id: number
  name: string
}

export const deliveryRunKeys = {
  all: ['delivery-runs'] as const,
  list: (storeId?: number, statuses?: string) =>
    [...deliveryRunKeys.all, 'list', storeId, statuses] as const,
  detail: (id: number) => [...deliveryRunKeys.all, 'detail', id] as const,
  drivers: (storeId?: number) =>
    [...deliveryRunKeys.all, 'drivers', storeId] as const,
}

// Invalidate the run lists/details AND the Deliveries tab so an order's
// assigned-run badge (delivery_run on the search payload) refreshes.
function invalidateRuns(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: deliveryRunKeys.all })
  queryClient.invalidateQueries({ queryKey: [...receiptKeys.all, 'orders'] })
}

// GET /delivery-runs?status=... — runs for the store. Defaults (no statuses)
// to the BE's active set (open + started).
export function useDeliveryRuns(statuses?: DeliveryRunStatus[]) {
  const { selectedStore } = useSessionStore()
  const statusParam = statuses?.join(',')

  return useQuery({
    queryKey: deliveryRunKeys.list(selectedStore?.id, statusParam),
    queryFn: async (): Promise<DeliveryRun[]> => {
      const data = await v2api.get<{ runs: DeliveryRun[] }>(
        v2('/delivery-runs'),
        statusParam ? { status: statusParam } : undefined,
      )
      return data.runs ?? []
    },
    enabled: !!selectedStore?.id,
    staleTime: 1000 * 15,
  })
}

// GET /delivery-runs/{id} — run detail with ordered stops + maps deep link.
export function useDeliveryRun(runId: number | null) {
  return useQuery({
    queryKey: deliveryRunKeys.detail(runId ?? -1),
    queryFn: async (): Promise<DeliveryRun> => {
      return v2api.get<DeliveryRun>(v2(`/delivery-runs/${runId}`))
    },
    enabled: !!runId,
  })
}

// GET /delivery-runs/drivers — candidate drivers (active store staff).
export function useDeliveryRunDrivers() {
  const { selectedStore } = useSessionStore()

  return useQuery({
    queryKey: deliveryRunKeys.drivers(selectedStore?.id),
    queryFn: async (): Promise<DeliveryRunDriver[]> => {
      const data = await v2api.get<{ drivers: DeliveryRunDriver[] }>(
        v2('/delivery-runs/drivers'),
      )
      return data.drivers ?? []
    },
    enabled: !!selectedStore?.id,
    staleTime: 1000 * 60,
  })
}

// POST /delivery-runs — create an open run.
export function useCreateDeliveryRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      name: string
      driver_id?: number | null
    }): Promise<DeliveryRun> => {
      return v2api.post<DeliveryRun>(v2('/delivery-runs'), payload)
    },
    onSuccess: () => invalidateRuns(queryClient),
  })
}

// POST /delivery-runs/{id}/orders — add delivery orders to an open run.
export function useAddOrdersToRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      runId,
      receiptIds,
    }: {
      runId: number
      receiptIds: number[]
    }): Promise<DeliveryRun> => {
      return v2api.post<DeliveryRun>(v2(`/delivery-runs/${runId}/orders`), {
        receipt_ids: receiptIds,
      })
    },
    onSuccess: () => invalidateRuns(queryClient),
  })
}

// DELETE /delivery-runs/{id}/orders/{runOrderId} — remove a stop (open runs).
export function useRemoveOrderFromRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      runId,
      runOrderId,
    }: {
      runId: number
      runOrderId: number
    }): Promise<DeliveryRun> => {
      return v2api.delete<DeliveryRun>(
        v2(`/delivery-runs/${runId}/orders/${runOrderId}`),
      )
    },
    onSuccess: () => invalidateRuns(queryClient),
  })
}

// POST /delivery-runs/{id}/optimize — Google route optimization.
export function useOptimizeRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (runId: number): Promise<DeliveryRun> => {
      return v2api.post<DeliveryRun>(v2(`/delivery-runs/${runId}/optimize`))
    },
    onSuccess: () => invalidateRuns(queryClient),
  })
}

// POST /delivery-runs/{id}/start — lock the run; optimizes if not already.
export function useStartRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (runId: number): Promise<DeliveryRun> => {
      return v2api.post<DeliveryRun>(v2(`/delivery-runs/${runId}/start`))
    },
    onSuccess: () => invalidateRuns(queryClient),
  })
}

// POST /delivery-runs/{id}/complete — close the run.
export function useCompleteRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (runId: number): Promise<DeliveryRun> => {
      return v2api.post<DeliveryRun>(v2(`/delivery-runs/${runId}/complete`))
    },
    onSuccess: () => invalidateRuns(queryClient),
  })
}

// DELETE /delivery-runs/{id} — cancel a run (open or started).
export function useCancelRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (runId: number): Promise<{ status: string }> => {
      return v2api.delete<{ status: string }>(v2(`/delivery-runs/${runId}`))
    },
    onSuccess: () => invalidateRuns(queryClient),
  })
}
